import axios from 'axios'
import { api_url } from '../utils/utils'
import { socket } from '../utils/utils'

let csrfToken = null
let csrfTokenPromise = null
let csrfTokenFetchedAt = 0
const unsafeMethods = ['post', 'put', 'patch', 'delete']
const csrfTtlMs = 30 * 60 * 1000
const csrfExpiredMessage = 'Sessiya müddəti bitib. Zəhmət olmasa səhifəni yeniləyib yenidən cəhd edin.'
let privilegedLogoutInProgress = false
const isAuthenticationRequest = (url = '') => /\/(admin-login|admin\/login-(otp\/(verify|resend)|totp\/verify)|seller-login|seller\/login-otp\/(verify|resend)|logout)$/.test(url)
const isAuthenticationRestoration = (url = '') => /\/(admin\/login-(otp|totp)\/verify|seller\/login-otp\/verify|get-user)$/.test(url)
export const resetPrivilegedLogoutState = () => {
    privilegedLogoutInProgress = false
}
const handlePrivilegedSessionExpiry = () => {
    if (privilegedLogoutInProgress) return
    privilegedLogoutInProgress = true
    clearCsrfToken()
    if (socket.connected) socket.disconnect()
    socket.auth = {}
    window.dispatchEvent(new CustomEvent('privileged-session-expired'))
    const loginPath = window.location.pathname.startsWith('/admin/') ? '/admin/login' : '/login'
    if (window.location.pathname !== loginPath) {
        window.location.replace(loginPath)
    } else {
        // No navigation/reload occurs when already on login, so release the
        // concurrency latch for a later authenticated session in this runtime.
        setTimeout(resetPrivilegedLogoutState, 0)
    }
}
const isCsrfError = (response) =>
    response?.status === 403 &&
    (
        response?.data?.code === 'CSRF_INVALID' ||
        response?.data?.error === 'Invalid CSRF token'
    )
const csrfClient = axios.create({
    baseURL: api_url,
    withCredentials: true
})

const getCsrfToken = async () => {
    if (csrfToken && Date.now() - csrfTokenFetchedAt < csrfTtlMs) return csrfToken
    if (csrfTokenPromise) return csrfTokenPromise

    csrfTokenPromise = csrfClient.get('/api/csrf-token')
        .then(({ data }) => {
            csrfToken = data.csrfToken
            csrfTokenFetchedAt = Date.now()
            return csrfToken
        })
        .finally(() => {
            csrfTokenPromise = null
        })

    return csrfTokenPromise
}

const clearCsrfToken = () => {
    csrfToken = null
    csrfTokenFetchedAt = 0
}

const attachCsrfToken = async (config) => {
    const method = (config.method || 'get').toLowerCase()
    if (!unsafeMethods.includes(method) || config.url?.includes('/csrf-token')) return config

    const token = await getCsrfToken()
    config.headers = {
        ...(config.headers || {}),
        'X-CSRF-Token': token
    }
    return config
}

axios.defaults.withCredentials = true
axios.interceptors.request.use(attachCsrfToken)

const api = axios.create({
    baseURL: `${api_url}/api`,
    withCredentials: true,
    headers: {
        'X-Client-App': 'dashboard'
    }
})

api.interceptors.request.use(attachCsrfToken)

const handleAuthenticationResponse = (response) => {
    if (isAuthenticationRestoration(response?.config?.url)) resetPrivilegedLogoutState()
    return response
}

const handleAuthenticationError = (error) => {
    const { response, config } = error
    if (response?.status === 401 && !isAuthenticationRequest(config?.url)) {
        handlePrivilegedSessionExpiry()
    }
    return Promise.reject(error)
}

// Legacy reducers still use the default Axios client. Give those protected
// requests the same privileged-session failure behavior as the API instance.
axios.interceptors.response.use(handleAuthenticationResponse, handleAuthenticationError)

api.interceptors.response.use(
    handleAuthenticationResponse,
    async (error) => {
        const { response, config } = error
        const method = (config?.method || 'get').toLowerCase()

        if (response?.status === 401 && !isAuthenticationRequest(config?.url)) return handleAuthenticationError(error)

        if (
            unsafeMethods.includes(method) &&
            isCsrfError(response) &&
            !config._csrfRetry
        ) {
            clearCsrfToken()
            config._csrfRetry = true
            const token = await getCsrfToken()
            config.headers = {
                ...(config.headers || {}),
                'X-CSRF-Token': token
            }
            return api(config)
        }

        if (unsafeMethods.includes(method) && isCsrfError(response)) {
            response.data = {
                ...(response.data || {}),
                message: csrfExpiredMessage,
                error: csrfExpiredMessage
            }
        }

        return Promise.reject(error)
    }
)

export default api
